import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {FireplacesChimneyBreastUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    FireplacesChimneyBreast,
    FireplacesChimneyBreastOnServerCreated,
    selectFireplacesChimneyBreastsActionLoading,
    selectFireplacesChimneyBreastById,
    selectLastCreatedFireplacesChimneyBreastId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-fireplaces-chimney-breast-edit',
    templateUrl: './fireplaces-chimney-breast-edit.component.html',
    styleUrls: ['./fireplaces-chimney-breast-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})


export class FireplacesChimneyBreastEditComponent implements OnInit, OnDestroy {

    model: FireplacesChimneyBreast;
    oldModel: FireplacesChimneyBreast;

    fireplacesChimneyBreastForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    propertySubTypeId = null;
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param subheaderService: SubheaderService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectFireplacesChimneyBreastsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            this.readonly = data.readonly
            if (params.property_sub_type_id) {
                this.propertySubTypeId = Number(params.property_sub_type_id);
            }
            const id = params.id;
            if (id && id > 0) {
                this.store.pipe(select(selectFireplacesChimneyBreastById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initFireplacesChimneyBreast();
                    }
                });
            } else {
                this.model = new FireplacesChimneyBreast();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initFireplacesChimneyBreast();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Cubic Contents', page: `../default/admin-management/linked-tables/base-of-values`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/base-of-values/edit/${id}`
                    : `../default/admin-management/linked-tables/base-of-values/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initFireplacesChimneyBreast() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.fireplacesChimneyBreastForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Cubic Content '${
                this.model.name
                }'`;
        }

        return 'New Cubic Content';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.fireplacesChimneyBreastForm.controls[controlName];
        const result = control.invalid && control.touched;
        return result;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.fireplacesChimneyBreastForm.markAsPristine();
        this.fireplacesChimneyBreastForm.markAsUntouched();
        this.fireplacesChimneyBreastForm.updateValueAndValidity();
    }

    prepareFireplacesChimneyBreast(): FireplacesChimneyBreast {
        const controls = this.fireplacesChimneyBreastForm.controls;
        const _model = new FireplacesChimneyBreast();
        _model.clear();
        _model.id = this.model.id;
        _model.name = controls.name.value;
        _model.property_sub_type_id = this.propertySubTypeId;
        _model.is_hidden = this.model.is_hidden

        return _model;
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.fireplacesChimneyBreastForm.controls;
        /** check form */
        if (this.fireplacesChimneyBreastForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        const editedFacility = this.prepareFireplacesChimneyBreast();
        if (this.model.id > 0) {
            this.updateFireplacesChimneyBreast(editedFacility);
        } else {
            this.createFireplacesChimneyBreast(editedFacility);
        }
    }

    /**
     * Update model
     *
     * @param _model: FireplacesChimneyBreastModel
     */
    updateFireplacesChimneyBreast(_model: FireplacesChimneyBreast) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateFireplacesChimneyBreast: Update<FireplacesChimneyBreast> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new FireplacesChimneyBreastUpdated({
            partialFireplacesChimneyBreast: updateFireplacesChimneyBreast,
            fireplacesChimneyBreast: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: FireplacesChimneyBreastModel
     */
    createFireplacesChimneyBreast(_model: FireplacesChimneyBreast) {
        this.store.dispatch(new FireplacesChimneyBreastOnServerCreated({fireplacesChimneyBreast: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedFireplacesChimneyBreastId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }

}
